import { getAccessToken } from '../authentication/supabaseService';

const API_BASE = '/api/admin';

export interface AdminSetupUser {
  id: string;
  email: string;
  name: string | null;
}

export interface SetupPlatform {
  id: string;
  platformName: string;
  enabled: boolean;
  orderEmails: string[] | null;
  orderClientNames: string[] | null;
  /**
   * Van Gelder only — which document world this connection lives in (resolved server-side).
   * null on every other platform, which is what tells the UI not to render the toggle.
   */
  vangelderPakbonMode: boolean | null;
}

export interface SetupSection {
  id: string;
  name: string;
  restaurantSectionType: string;
  platforms: SetupPlatform[];
}

export interface SetupRevenueSource {
  id: string;
  revenueSourceType: string;
  revenueSourceName: string;
  restaurantName: string;
  relevantSections: string[];
}

export interface SetupRestaurant {
  id: string;
  name: string;
  sections: SetupSection[];
  revenueSources: SetupRevenueSource[];
}

export interface SetupProfile {
  restaurants: SetupRestaurant[];
}

async function fetchWithAuth(endpoint: string, options: RequestInit = {}) {
  const token = await getAccessToken();
  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body.error || `Failed to fetch ${endpoint}: ${response.statusText}`);
  }

  return response.json();
}

export async function getAdminSetupUsers(): Promise<AdminSetupUser[]> {
  return fetchWithAuth('/setup/users');
}

export async function getAdminSetupProfile(userId: string): Promise<SetupProfile> {
  return fetchWithAuth(`/setup/profile/${userId}`);
}

export async function createSetupSection(data: {
  restaurantId: string; name: string; restaurantSectionType: 'FOOD' | 'DRINKS' | 'NON_FOOD';
}): Promise<Pick<SetupSection, 'id' | 'name' | 'restaurantSectionType'>> {
  return fetchWithAuth('/setup/sections', { method: 'POST', body: JSON.stringify(data) });
}

export async function createSetupPlatform(data: {
  restaurantSectionId: string;
  platformName: string;
  orderEmails?: string[];
  orderClientNames?: string[];
}): Promise<void> {
  return fetchWithAuth('/setup/platforms', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSetupPlatform(
  id: string,
  data: { orderEmails: string[]; orderClientNames: string[] }
): Promise<void> {
  return fetchWithAuth(`/setup/platforms/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

/**
 * Resolved behaviour flags of ONE restaurant_platform. Used by the super-admin-only "Platform
 * settings" block in the client-facing edit-platform modal, which has an id but not the setup tree.
 * `vangelderPakbonMode` is null on every non-vangelder platform — that is what tells the UI to render
 * nothing for that row. `combinedOrderingAccount` is not platform-gated, so it is never null.
 */
export interface SetupPlatformSettings {
  id: string;
  platformName: string;
  vangelderPakbonMode: boolean | null;
  combinedOrderingAccount: boolean;
}

export async function fetchSetupPlatformSettings(id: string): Promise<SetupPlatformSettings> {
  return fetchWithAuth(`/setup/platforms/${id}/settings`);
}

/**
 * Flip per-connection behaviour flags (restaurant_platforms.settings). Merges server-side, so a
 * partial patch never clobbers a key it does not name — send only what actually moved.
 */
export async function updateSetupPlatformSettings(
  id: string,
  data: { vangelderPakbonMode?: boolean; combinedOrderingAccount?: boolean }
): Promise<SetupPlatformSettings> {
  return fetchWithAuth(`/setup/platforms/${id}/settings`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteSetupPlatform(id: string): Promise<void> {
  return fetchWithAuth(`/setup/platforms/${id}`, { method: 'DELETE' });
}

export async function createSetupRevenueSource(data: {
  restaurantId: string;
  revenueSourceType: string;
  revenueSourceName: string;
  restaurantName: string;
  relevantSections: string[];
}): Promise<void> {
  return fetchWithAuth('/setup/revenue-sources', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateSetupRevenueSource(
  id: string,
  data: {
    revenueSourceType: string;
    revenueSourceName: string;
    restaurantName: string;
    relevantSections: string[];
  }
): Promise<void> {
  return fetchWithAuth(`/setup/revenue-sources/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteSetupRevenueSource(id: string): Promise<void> {
  return fetchWithAuth(`/setup/revenue-sources/${id}`, { method: 'DELETE' });
}
